import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/* eslint-disable import/no-extraneous-dependencies */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { ToastAlert } from "./ToastAlert";
import { useTimeout } from "../../hooks/useTimeout";
import { easeCurves } from "../../motion/eases";
import { theme } from "../../theme";
import useResizeObserver from 'use-resize-observer';
import { usePresenceAnimation } from "../../hooks/usePresenceAnimation";
var EXIT_TIMEOUT = null;
var UNMOUNT_TRANSITION_DURATION = 200;
/**
 * ToastItem
 *
 * Handles Toast animations and mounting / unmounting, while
 * accepting either a string, render function, or react node
 * to render as its content.
 */

export function ToastItem(_ref) {
  var toast = _ref.toast,
      precedingHeight = _ref.precedingHeight,
      registerHeight = _ref.registerHeight,
      removeToast = _ref.removeToast,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'top' : _ref$placement; // Track this toast item's height

  var ref = useRef(null);
  useResizeObserver({
    ref: ref,
    onResize: function onResize(_ref2) {
      var height = _ref2.height;
      registerHeight(height);
    }
  });
  var message = toast.message,
      _toast$duration = toast.duration,
      duration = _toast$duration === void 0 ? EXIT_TIMEOUT : _toast$duration;
  var isTop = placement.indexOf('top') > -1; // Tell our toast provider to close this toast

  var close = useCallback(function () {
    removeToast(toast.id);
  }, [removeToast, toast.id]);
  /**
   * Handle delaying closure of a toast when the user
   * cursor is hovering over it.
   */

  var _useState = useState(duration),
      _useState2 = _slicedToArray(_useState, 2),
      timeout = _useState2[0],
      setTimeout = _useState2[1];

  useTimeout(close, timeout);
  var handleMouseEnter = useCallback(function () {
    setTimeout(null);
  }, []);
  var handleMouseLeave = useCallback(function () {
    setTimeout(duration);
  }, [duration]);
  /**
   * Render a toast using either a render callback,
   * a string, or jsx.
   */

  var renderedToast = useMemo(function () {
    if (typeof message === 'function') {
      return message(close);
    }

    if (typeof message === 'string') {
      return /*#__PURE__*/React.createElement(ToastAlert, {
        onRequestClose: close,
        title: message
      });
    }

    if (React.isValidElement(message)) {
      return message;
    }

    if (_typeof(message) === 'object') {
      var _ref3 = message,
          disableClose = _ref3.disableClose,
          other = _objectWithoutProperties(_ref3, ["disableClose"]);

      return /*#__PURE__*/React.createElement(ToastAlert, _extends({}, other, {
        onRequestClose: !disableClose && close
      }));
    }

    return message;
  }, [message, close]);
  var multiplier = isTop ? 1 : -1;
  var style = usePresenceAnimation({
    exitDuration: UNMOUNT_TRANSITION_DURATION,
    styles: {
      initial: {
        transform: "translateY(".concat((precedingHeight - 24) * multiplier, "px)"),
        opacity: 0
      },
      animate: {
        transition: "all 250ms ".concat(easeCurves.primary),
        transform: "translateY(".concat(precedingHeight * multiplier, "px)"),
        opacity: 1
      },
      exit: {
        transition: "opacity ".concat(UNMOUNT_TRANSITION_DURATION, "ms ").concat(easeCurves.primary, ", transform ").concat(UNMOUNT_TRANSITION_DURATION, "ms ").concat(easeCurves.accelerate),
        transform: "translateY(".concat((precedingHeight - 24) * multiplier, "px)"),
        opacity: 0
      }
    }
  });
  return /*#__PURE__*/React.createElement(ToastItemWrapper, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    $isTop: isTop,
    ref: ref
  }, /*#__PURE__*/React.createElement(Content, {
    style: style
  }, renderedToast));
}
var Content = styled.div.withConfig({
  displayName: "ToastItem__Content",
  componentId: "vapor__p9uu35-0"
})(["pointer-events:auto;will-change:transform,opacity;"]);
var ToastItemWrapper = styled.div.withConfig({
  displayName: "ToastItem__ToastItemWrapper",
  componentId: "vapor__p9uu35-1"
})(["pointer-events:none;box-sizing:border-box;position:absolute;user-select:none;", "{padding:", ";}"], Content, function (p) {
  return p.$isTop ? "".concat(theme.spaces[8], " ").concat(theme.spaces[8], " 0 ").concat(theme.spaces[8]) : "0 ".concat(theme.spaces[8], " ").concat(theme.spaces[8], " ").concat(theme.spaces[8]);
});